/**
 * CartContext — the shopping cart's state, shared across the whole app.
 *
 * WHY CONTEXT + useReducer (and not useState in a component)
 * ---------------------------------------------------------
 * The cart is needed in MANY places at once: the Add button on a product card,
 * the floating "Go to cart" bar, the cart page, the navbar badge. If the cart
 * lived in one component's useState, none of the others could read it. Context
 * lifts it above the whole tree so every component shares ONE cart.
 *
 * useReducer (not useState) because cart updates are a small set of well-defined
 * ACTIONS — add, increment, decrement, remove, clear. A reducer keeps all that
 * logic in one predictable place instead of scattered setState calls.
 *
 * PERSISTENCE: the cart is mirrored to localStorage, so a page refresh (or
 * coming back tomorrow) keeps the cart. This is the storefront behaviour users
 * expect.
 *
 * NOTE: this is a client-only cart (Milestone 9 territory, pulled forward so
 * the redesigned UI actually works). Prices here are for display; the real
 * order total will always be recomputed on the SERVER at checkout — never trust
 * a price the browser sends.
 */

import { createContext, useContext, useReducer, useEffect } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'rocket07_cart';

/**
 * State shape: { items: { [productId]: { product, quantity } } }
 * Keyed by id so lookups ("how many of product 7?") are instant.
 */
const initialState = { items: {} };

function cartReducer(state, action) {
  switch (action.type) {
    case 'HYDRATE':
      return action.payload ?? initialState;

    case 'ADD': {
      const { product } = action;
      const existing = state.items[product.id];
      return {
        items: {
          ...state.items,
          [product.id]: {
            product,
            quantity: (existing?.quantity ?? 0) + 1,
          },
        },
      };
    }

    case 'DECREMENT': {
      const existing = state.items[action.id];
      if (!existing) return state;

      // Dropping below 1 removes the line entirely.
      if (existing.quantity <= 1) {
        const next = { ...state.items };
        delete next[action.id];
        return { items: next };
      }
      return {
        items: {
          ...state.items,
          [action.id]: { ...existing, quantity: existing.quantity - 1 },
        },
      };
    }

    case 'REMOVE': {
      const next = { ...state.items };
      delete next[action.id];
      return { items: next };
    }

    case 'CLEAR':
      return initialState;

    default:
      return state;
  }
}

/**
 * Lazy initialiser — reads localStorage ONCE when the provider first mounts,
 * so the cart is already populated on the very first render (no flash of empty).
 */
function readInitialState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { items: JSON.parse(raw) } : initialState;
  } catch {
    return initialState;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, undefined, readInitialState);

  // Persist on every change. Runs after render, so it never blocks the UI.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
    } catch {
      // Storage full or blocked (private mode) — the cart still works in memory.
    }
  }, [state.items]);

  // Derived values, computed once per render and shared to consumers.
  const lines = Object.values(state.items);
  const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);
  const subtotal = lines.reduce((sum, line) => sum + line.product.price * line.quantity, 0);
  const mrpTotal = lines.reduce((sum, line) => sum + line.product.mrp * line.quantity, 0);
  const savings = mrpTotal - subtotal;

  const value = {
    items: state.items,
    lines,
    itemCount,
    subtotal,
    savings,
    addItem: (product) => dispatch({ type: 'ADD', product }),
    increment: (product) => dispatch({ type: 'ADD', product }),
    decrement: (id) => dispatch({ type: 'DECREMENT', id }),
    removeItem: (id) => dispatch({ type: 'REMOVE', id }),
    clear: () => dispatch({ type: 'CLEAR' }),
    getQuantity: (id) => state.items[id]?.quantity ?? 0,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/**
 * useCart — the hook every component uses to read or change the cart.
 * Throws if used outside the provider, which turns a silent bug into a clear
 * error message during development.
 *
 * Co-located with the provider on purpose (provider + its hook belong together).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used inside a <CartProvider>');
  }
  return context;
}

export default CartContext;
